import React from "react";
import { X, FileText, Calendar, Database, Zap, Clock, CheckCircle, XCircle, Loader } from "lucide-react";
import { UploadedFile } from "../../services/uploaded-files.service";

interface FileLogsModalProps {
  file: UploadedFile | null;
  onClose: () => void;
}

const FileLogsModal: React.FC<FileLogsModalProps> = ({ file, onClose }) => {
  if (!file) return null;

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return "Invalid Date";
    }
  };

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds.toFixed(2)}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds.toFixed(0)}s`;
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
      DONE: { color: "bg-green-100 text-green-800", icon: <CheckCircle className="w-3 h-3" />, label: "Done" },
      IN_PROGRESS: { color: "bg-blue-100 text-blue-800", icon: <Loader className="w-3 h-3 animate-spin" />, label: "In Progress" },
      NOT_STARTED: { color: "bg-gray-100 text-gray-800", icon: <Clock className="w-3 h-3" />, label: "Not Started" },
      FAILED: { color: "bg-red-100 text-red-800", icon: <XCircle className="w-3 h-3" />, label: "Failed" },
    };

    const config = statusConfig[status] || statusConfig.NOT_STARTED;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">File Processing Logs</h2>
              <p className="text-sm text-gray-500 truncate max-w-md">{file.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* File Information */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">File Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500">File Type</p>
                <p className="text-sm font-medium text-gray-900">{file.file_type}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">File Size</p>
                <p className="text-sm font-medium text-gray-900">{formatFileSize(file.file_size)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Pages</p>
                <p className="text-sm font-medium text-gray-900">{file.file_pages || 0}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Upload Type</p>
                <p className="text-sm font-medium text-gray-900">{file.upload_type}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">AI Performance</p>
                <p className="text-sm font-medium text-gray-900">{file.ai_performance_level}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Processing Type</p>
                <p className="text-sm font-medium text-gray-900">{file.file_processing_type}</p>
              </div>
            </div>
          </div>

          {/* Processing Status Overview */}
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center gap-2">
              <Database className="w-4 h-4" />
              Processing Status
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-2">Overall Status</p>
                {getStatusBadge(file.overall_processed_status)}
              </div>
              <div className="bg-white rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-2">Visual Processing</p>
                {getStatusBadge(file.visual_processing_status)}
              </div>
              <div className="bg-white rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-2">RAG Processing</p>
                {getStatusBadge(file.rag_processing_status)}
              </div>
            </div>
          </div>

          {/* Visual Processing Details */}
          {file.visual_processing && (
            <div className="border border-gray-200 rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center gap-2">
                <Zap className="w-4 h-4 text-blue-600" />
                Visual Processing Details
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Start Time</p>
                  <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                    <Calendar className="w-3 h-3 text-gray-400" />
                    {formatDate(file.visual_processing.start_time)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">End Time</p>
                  <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                    <Calendar className="w-3 h-3 text-gray-400" />
                    {formatDate(file.visual_processing.end_time)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Time Taken</p>
                  <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                    <Clock className="w-3 h-3 text-gray-400" />
                    {formatDuration(file.visual_processing.time_taken)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <div className="mt-1">
                    {getStatusBadge(file.visual_processing.status)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* RAG Processing Details */}
          {file.rag_processing && (
            <div className="border border-gray-200 rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center gap-2">
                <Zap className="w-4 h-4 text-purple-600" />
                RAG Processing Details
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Start Time</p>
                  <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                    <Calendar className="w-3 h-3 text-gray-400" />
                    {formatDate(file.rag_processing.start_time)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">End Time</p>
                  <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                    <Calendar className="w-3 h-3 text-gray-400" />
                    {formatDate(file.rag_processing.end_time)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Time Taken</p>
                  <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                    <Clock className="w-3 h-3 text-gray-400" />
                    {formatDuration(file.rag_processing.time_taken)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <div className="mt-1">
                    {getStatusBadge(file.rag_processing.status)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Overall Processing Details */}
          {file.overall_processing && (
            <div className="border border-gray-200 rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide flex items-center gap-2">
                <Zap className="w-4 h-4 text-green-600" />
                Overall Processing Details
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Start Time</p>
                  <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                    <Calendar className="w-3 h-3 text-gray-400" />
                    {formatDate(file.overall_processing.start_time)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">End Time</p>
                  <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                    <Calendar className="w-3 h-3 text-gray-400" />
                    {formatDate(file.overall_processing.end_time)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Time Taken</p>
                  <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                    <Clock className="w-3 h-3 text-gray-400" />
                    {formatDuration(file.overall_processing.time_taken)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Status</p>
                  <div className="mt-1">
                    {getStatusBadge(file.overall_processing.status)}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* File Summary */}
          {file.file_summary && (
            <div className="border border-gray-200 rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">File Summary</h3>
              <div className="bg-gray-50 rounded p-3 max-h-40 overflow-y-auto">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{file.file_summary}</p>
              </div>
            </div>
          )}

          {/* Timestamps */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Timestamps</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-500">Created At</p>
                <p className="text-sm font-medium text-gray-900">{formatDate(file.createdAt)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Updated At</p>
                <p className="text-sm font-medium text-gray-900">{formatDate(file.updatedAt)}</p>
              </div>
              {file.uploaded_at && (
                <div>
                  <p className="text-xs text-gray-500">Uploaded At</p>
                  <p className="text-sm font-medium text-gray-900">{formatDate(file.uploaded_at)}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default FileLogsModal;
